/**
 * WebGLRenderer wrapper with auto-tier adaptive settings.
 * Handles resize, pixel ratio cap, tone mapping, and exposure.
 */

import * as THREE from "three";
import type { DeviceProfile } from "@/types";

export function createRenderer(container: HTMLElement, profile: DeviceProfile): THREE.WebGLRenderer {
  const renderer = new THREE.WebGLRenderer({
    antialias: profile.antialias,
    // alpha:true => canvas transparent wherever nothing is drawn, so the studio path
    // (walls removed, scene.background=null) composites onto the host --nv-bg surface.
    alpha: true,
    powerPreference: profile.gpuTier >= 2 ? "high-performance" : "default",
    // false: nothing reads this canvas back (cover uses model-viewer.toBlob, AR uses
    // GLTFExporter — neither touches this context). Keeping the back-buffer on tiled
    // mobile GPUs defeats fast tile-discard and costs bandwidth every frame.
    preserveDrawingBuffer: false,
    stencil: false,
    depth: true,
  });

  renderer.setPixelRatio(profile.pixelRatio);
  const rect = container.getBoundingClientRect();
  renderer.setSize(rect.width, rect.height, false);

  renderer.outputColorSpace = THREE.SRGBColorSpace;
  // model-viewer @3.5.0 renders with ACESFilmicToneMapping (NOT Khronos PBR
  // Neutral — that only became its default in v4.0). ACES gives the warm filmic
  // shoulder/contrast the reference shows; Neutral reads flat/washed. This is the
  // single biggest fix for the "too flat" regression. Exposure stays 1.0 (the
  // prod exposure="1"); per-product config can override via ViewerOptions.exposure.
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;

  // No shadow map: model-viewer's ground shadow is a blurred-depth CONTACT shadow
  // rendered to a texture (see Lighting.ContactShadow), not a directional shadow
  // map. We deliberately leave shadowMap disabled so nothing casts a hard VSM
  // shadow that would fight the soft contact patch.

  container.appendChild(renderer.domElement);
  renderer.domElement.style.touchAction = "none";
  return renderer;
}

export function resizeRendererToDisplaySize(
  renderer: THREE.WebGLRenderer,
  camera: THREE.PerspectiveCamera,
  container: HTMLElement,
): boolean {
  const rect = container.getBoundingClientRect();
  const width = Math.max(1, Math.floor(rect.width));
  const height = Math.max(1, Math.floor(rect.height));
  const canvas = renderer.domElement;
  const needResize = canvas.width !== width * renderer.getPixelRatio() || canvas.height !== height * renderer.getPixelRatio();
  if (needResize) {
    renderer.setSize(width, height, false);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
  }
  return needResize;
}
